import React, { useMemo } from 'react';
import { Box, Modal, ModalBody, Text, Stack } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import type { AuditLogEntry } from '../types';
import { formatAttributeName } from '../utils/formatting';
import { diffViewerStyles } from './diff-theme';

interface DiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  log: AuditLogEntry | null;
}

// Layout constants for consistent sizing
const DIFF_VIEWER_MAX_HEIGHT = '500px';
const LEGEND_SWATCH_SIZE = '16px';

/**
 * Recursively sort object keys for consistent comparison
 */
function sortObjectKeys(object: unknown): unknown {
  if (object == null || typeof object !== 'object') {
    return object;
  }

  if (Array.isArray(object)) {
    return object.map(sortObjectKeys);
  }

  const sortedObject: Record<string, unknown> = {};
  const keys = Object.keys(object as Record<string, unknown>).sort();
  for (const key of keys) {
    sortedObject[key] = sortObjectKeys((object as Record<string, unknown>)[key]);
  }
  return sortedObject;
}

/**
 * Format JSON for diff display - sorts keys recursively for consistent comparison.
 * Falls back to unsorted JSON if sorting fails (e.g., circular references).
 * Silent fallback is acceptable since the diff viewer will still work with unsorted JSON.
 */
function formatJsonForDiff(data: unknown): string {
  if (data == null) {
    return '{}';
  }
  try {
    const sortedData = sortObjectKeys(data);
    return JSON.stringify(sortedData, null, 2);
  } catch {
    // Fallback to unsorted JSON if sorting fails (e.g., circular references)
    return JSON.stringify(data, null, 2);
  }
}

/**
 * Get a human-readable title for the diff based on action type
 */
function getDiffTitle(action: string): string {
  const actionLower = action.toLowerCase();
  switch (actionLower) {
    case 'created':
      return 'Created with values';
    case 'deleted':
      return 'Deleted values';
    case 'updated':
      return 'Changes';
    default:
      return 'Diff';
  }
}

/**
 * Check if a value is a plain object (not null, not array, not primitive).
 * Used to safely determine if Object.keys can be called on a value.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Safely get the number of keys in a value, returning 0 for non-objects.
 */
function getObjectKeyCount(value: unknown): number {
  if (!isPlainObject(value)) {
    return 0;
  }
  return Object.keys(value).length;
}

export const DiffModal: React.FC<DiffModalProps> = ({ isOpen, onClose, log }) => {
  const { oldValue, newValue, hasChanges } = useMemo(() => {
    if (log == null) {
      return { oldValue: '', newValue: '', hasChanges: false };
    }

    const actionLower = log.action.toLowerCase();
    const changedFrom = log.changed_from ?? {};
    const changedTo = log.changed_to ?? {};

    // For created actions: show empty -> new values
    if (actionLower === 'created') {
      return {
        oldValue: '{}',
        newValue: formatJsonForDiff(changedTo),
        hasChanges: getObjectKeyCount(changedTo) > 0,
      };
    }

    // For deleted actions: show old values -> empty
    if (actionLower === 'deleted') {
      return {
        oldValue: formatJsonForDiff(changedFrom),
        newValue: '{}',
        hasChanges: getObjectKeyCount(changedFrom) > 0,
      };
    }

    // For updated actions: show old -> new
    return {
      oldValue: formatJsonForDiff(changedFrom),
      newValue: formatJsonForDiff(changedTo),
      hasChanges: getObjectKeyCount(changedFrom) > 0 || getObjectKeyCount(changedTo) > 0,
    };
  }, [log]);

  if (log == null) {
    return null;
  }

  const title = `${getDiffTitle(log.action)} - ${formatAttributeName(log.object_type)} #${log.object_id}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <ModalBody>
        {!hasChanges ? (
          <Box p="400" textAlign="center">
            <Text color="subtle">No changes to display.</Text>
          </Box>
        ) : (
          <Stack gap="200">
            {/* Legend */}
            <Box display="flex" gap="400" pb="200">
              <Box display="flex" alignItems="center" gap="100">
                <Box
                  style={{
                    width: LEGEND_SWATCH_SIZE,
                    height: LEGEND_SWATCH_SIZE,
                    backgroundColor: themeVars.color['surface-critical-subtle'],
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                    borderRadius: themeVars.radius['2'],
                  }}
                />
                <Text size="xs" color="subtle">
                  Removed
                </Text>
              </Box>
              <Box display="flex" alignItems="center" gap="100">
                <Box
                  style={{
                    width: LEGEND_SWATCH_SIZE,
                    height: LEGEND_SWATCH_SIZE,
                    backgroundColor: themeVars.color['surface-success-minimal'],
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                    borderRadius: themeVars.radius['2'],
                  }}
                />
                <Text size="xs" color="subtle">
                  Added
                </Text>
              </Box>
            </Box>

            {/* Diff Viewer */}
            <Box
              overflowX="hidden"
              overflowY="auto"
              style={{
                border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                borderRadius: themeVars.radius['4'],
                maxHeight: DIFF_VIEWER_MAX_HEIGHT,
              }}
            >
              <ReactDiffViewer
                oldValue={oldValue}
                newValue={newValue}
                splitView={false}
                useDarkTheme={false}
                showDiffOnly={false}
                compareMethod={DiffMethod.LINES}
                styles={diffViewerStyles}
                hideLineNumbers={false}
              />
            </Box>
          </Stack>
        )}
      </ModalBody>
    </Modal>
  );
};
